import * as _ from 'lodash';
import * as hash from 'object-hash';
import { Component } from 'react';
import { Dispatch, connect } from "react-redux";
import { AppActionCreators, IAppState } from '@nkr/store/application';
import { IBaseResponse, IFileUpload } from '@nkr/models/index';
import { IDonorProfileAddress, IDonorProfileAddressLookupData, IDonorProfileHeaderInfo, IDonorProfileRecords } from '@nkr/models/index';
import { DonorActionCreators, FileActionCreators, IAmbraDirectToViewerResponse, IApplicationState, IDonorProfileRecordsResponse, IDonorProfileUploadResponse } from '@nkr/store/index';
import { FileDocumentType, FileDocumentTypeDescription } from '@nkr/enums/document-type';
import { toast } from 'react-toastify';
import { IBaseDispatchProps, ToggleType } from '../models';
import { downloadFile } from '@nkr/services/download-file';
import DuplicateRecordModal from '@nkr/components/duplicate-record-modal';
import RecordsContent from './records-content';

interface IOwnProps {
    centerId: number,
    activeKey: string,
    isAdmin: boolean,
    donorId: number,
    headerInfo: IDonorProfileHeaderInfo,
    hasDataChanged: boolean,
    saveRequested: boolean,
    refreshRequested: boolean,
    onDataChanged: (hasDataChanged: boolean) => void,
    onSaveFinished: (refreshHeader: boolean) => void,
    onRefreshFinished: () => void,
    onResetDataChangedWarning: () => void,
    onTabClick: (href: string) => void,
    onUpdateForErrors: (toggleType: ToggleType, messages: string[]) => void
}

interface IStateProps {
    app: IAppState,
    groupIsSuper: boolean
}

interface IDispatchProps extends IBaseDispatchProps {
    getRecordsData: (id: number, centerId: number) => Promise<IDonorProfileRecordsResponse>,
    downloadFile: (docType: number, lookupId: number) => Promise<any>,
    downloadChecklist: (donorId: number) => Promise<any>,
    uploadFile: (centerId: number, donorId: number, docType: number, file: IFileUpload, append: boolean) => Promise<IDonorProfileUploadResponse>,
    getAmbraLink: (donor: number) => Promise<IAmbraDirectToViewerResponse>,
    deleteAmbraCtScan: (donorId: number) => Promise<IAmbraDirectToViewerResponse>,
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    data: IDonorProfileRecords,
    appendMedChart: boolean,
    addressData: IDonorProfileAddress,
    addressLookupData: IDonorProfileAddressLookupData,
    donorDataHash: string,
    files: IFileUpload[],
    showDuplicateRecord: boolean,
    duplicateMessage: string
}

const initialState: IState = {
    files: [],
    data: null,
    appendMedChart: true,
    addressData: null,
    addressLookupData: {
        stateList: [],
        countryList: []
    },
    donorDataHash: '',
    showDuplicateRecord: false,
    duplicateMessage: ''
}

class Records extends Component<TProps, IState> {
    readonly state: IState = initialState;

    componentDidMount = () => {
        this.getData();
    }

    componentWillReceiveProps = (nextProps: Readonly<TProps>, nextContext: any) => {
        if (this.props.saveRequested == false && nextProps.saveRequested == true) {
            this.confirmFileUploads();
        }

        if (this.props.refreshRequested == false && nextProps.refreshRequested == true) {
            this.getData();
        }
    }

    getData = () => {
        this.props.showLoading('Getting records, one moment please...').then(() => {
            Promise.all([
                this.props.getRecordsData(this.props.centerId, this.props.donorId)
            ])
                .then(([response]) => {
                    if (response.data.success) {
                        let data = response.data.results;
                        let donorDataHash = hash(data);

                        this.setState({
                            data,
                            donorDataHash,
                        }, () => {
                            if (this.props.refreshRequested) {
                                this.props.onRefreshFinished();
                            }
                        });
                    }
                })
                .catch(error => {
                    console.log(error);
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    refreshList = () => {
        this.props.getRecordsData(this.props.centerId, this.props.donorId).then(response => {
            if (response.data.success) {
                let data = response.data.results;
                let donorDataHash = hash(data);

                this.setState({
                    data,
                    donorDataHash,
                    files: []
                }, () => {
                    this.props.onDataChanged(false);
                });
            }
        });
    }

    handleDeleteCtScanAmbra = (donorId: number) => {
        this.props.showLoading('Deleting Ct Scan, one moment please...').then(() => {
            Promise.all([
                this.props.deleteAmbraCtScan(donorId)
            ])
                .then(([dataResponse]) => {
                    this.props.hideLoading();
                    toast.success("Ct Scan record deleted");
                })
                .catch(error => {
                    console.log(error);
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleFileUpload = (docType: FileDocumentType, file: File) => {
        let pendingUploads = _.cloneDeep(this.state.files);

        let newFile = [file];
        let fileExists = pendingUploads.find(x => x.docType == docType);

        if (fileExists) {
            pendingUploads.map(x => {
                if (x.docType == docType) {
                    x.file = newFile
                }
            });
        } else {
            let tmpFileData = {
                docType: docType,
                file: newFile
            } as IFileUpload;

            pendingUploads.push(tmpFileData);
        }

        let hasDataChanged = pendingUploads.length > 0;

        this.setState({
            files: pendingUploads,

        });

        this.props.onDataChanged(hasDataChanged);
    };

    handleRemoveFile = (docType: FileDocumentType) => {
        let newUploadsList = _.cloneDeep(this.state.files).filter(x => x.docType != docType);

        let hasDataChanged = newUploadsList.length > 0;

        this.setState({
            files: newUploadsList,
        });

        this.props.onDataChanged(hasDataChanged);
    }

    handleDataChange = (data: IDonorProfileRecords) => {
        let newData = _.cloneDeep(data);
        let newHash = hash(newData);

        let hasDataChanged = newHash != this.state.donorDataHash;

        this.setState({
            data: newData,
        });

        this.props.onDataChanged(hasDataChanged);
    }

    handleViewFile = (docType: FileDocumentType) => {
        this.props.downloadFile(docType, this.props.donorId).then((response: any) => {
            downloadFile(response.data, `${FileDocumentTypeDescription[docType]}.pdf`, response.headers.contentType)
        }).catch((error: IBaseResponse) => {
            toast.error("Unable to download file at this time.  Please contact support.");
        })
    }

    handleChecklistDownload = () => {
        this.props.downloadChecklist(this.props.donorId).then((response: any) => {
            downloadFile(response.data, 'activation-checklist.pdf', response.headers.contentType);
        }).catch((error: IBaseResponse) => {
            toast.error("Unable to download file at this time.  Please contact support.");
        });
    }

    confirmFileUploads = async () => {
        this.props.showLoading('Saving files, one moment please...').then(async () => {
            let promiseArr = [];

            this.state.files.map(fileUpload => {
                var appendFile = fileUpload.docType == FileDocumentType.DonorMedChart ? this.state.appendMedChart : false;

                promiseArr.push(this.props.uploadFile(this.props.centerId, this.props.donorId, fileUpload.docType, fileUpload, appendFile))
            });

            Promise.all<IBaseResponse>(promiseArr)
                .then((response) => {
                    let errTriggered = false;
                    response.forEach(resp => {
                        if (!resp.data.success && !errTriggered) {
                            let msg = "Unable to upload file.  Please try again.";

                            toast.error(msg);
                            errTriggered = true;
                        }
                    });

                    if (!errTriggered) {
                        this.props.onDataChanged(false);

                        toast.success("Upload successful.");
                    }

                    this.props.onSaveFinished(true);

                    this.getData();
                })
                .catch(error => {
                    if (error.response.status == 409) {
                        this.setState({
                            showDuplicateRecord: true,
                            duplicateMessage: error.response.data
                        }, () => {
                            this.props.onSaveFinished(false);
                        });
                    }
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleCloseDuplicateRecord = () => {
        this.setState({
            showDuplicateRecord: false
        });
    }

    handleMedChartToggle = (newValue: boolean) => {
        this.setState({
            appendMedChart: newValue
        });
    }

    render = () => {
        if (this.props.headerInfo == null)
            return null;

        let { data } = this.state;

        return (
            <>
                <RecordsContent
                    data={data}
                    files={this.state.files}
                    isSuperAdmin={this.props.isAdmin}
                    appendMedChart={this.state.appendMedChart}
                    onMedChartAppendToggle={this.handleMedChartToggle}
                    onViewFile={this.handleViewFile}
                    onDownloadChecklist={this.handleChecklistDownload}
                    onFileUpload={this.handleFileUpload}
                    onRemoveFile={this.handleRemoveFile}
                    getAmbraLink={this.props.getAmbraLink}
                    deleteCtScanAmbra={this.handleDeleteCtScanAmbra}
                />

                <DuplicateRecordModal
                    show={this.state.showDuplicateRecord}
                    message={this.state.duplicateMessage}
                    onClose={this.handleCloseDuplicateRecord}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app,
        groupIsSuper: appState.app.user.group.isSuper
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getRecordsData: (centerId, id) => {
            return dispatch(DonorActionCreators.getProfileRecordsData(id, centerId));
        },
        downloadFile: (docType, id) => {
            return dispatch(FileActionCreators.downloadFile(docType, id))
        },
        downloadChecklist: (donorId) => {
            return dispatch(FileActionCreators.downloadChecklist(donorId))
        },
        uploadFile: (centerId, donorId, docType, file, append) => {
            return dispatch(DonorActionCreators.uploadDonorProfileDocument(centerId, donorId, docType, file, append))
        },
        getAmbraLink: (donorId) => {
            return dispatch(DonorActionCreators.getAmbraLink(donorId));
        },
        deleteAmbraCtScan: (donorId) => {
            return dispatch(DonorActionCreators.deleteAmbraCtScan(donorId))
        },
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(Records);